import { Poster } from "PosterEditor/poster/Poster";
import styles from "./PosterStage.module.css";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { EditorContextType } from "PosterEditor/PosterEditor";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { useRef, useState, useEffect } from "react";
import { EXPANDED_HEIGHT_PERCENT } from "PosterEditor/sidebar/mobile-drawer";

interface PosterStageProps {
  onShowContextualEditor: (show: boolean) => void;
  contextType?: EditorContextType;
  isMobile?: boolean;
  isDrawerExpanded?: boolean;
  onDismissDrawer?: () => void;
}

export const PosterStage = (props: PosterStageProps) => {
  const showZoomControls = props.contextType !== "rendering" && !props.isMobile;
  const { project } = usePosterProject();
  const { screenSizeFromProject } = useSizePresets();
  const { editorUIState } = useEditorUI();
  const editingStaticLabel = editorUIState.editingElement?.staticLabel;
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const screenSize = screenSizeFromProject(project) ?? { width: 0, height: 0 };

  const paddingScale = 0.92; // Scale down slightly to create visual padding

  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current || !props.isMobile) {
        setScale(1);
        return;
      }

      const containerWidth = containerRef.current.clientWidth;
      // Use viewport height minus fixed elements (action bar ~60px + drawer ~80px + margins)
      // This gives us a stable height that doesn't change with poster size
      const availableHeight = window.innerHeight - 160;
      const posterWidth = screenSize.width;
      const posterHeight = screenSize.height;

      if (posterWidth > 0 && posterHeight > 0) {
        // Scale by whichever dimension is more constraining
        const widthScale = containerWidth / posterWidth;
        const heightScale = availableHeight / posterHeight;
        const fitScale = Math.min(widthScale, heightScale);
        const newScale = Math.min(fitScale * paddingScale, 1);
        setScale(newScale);
      } else {
        setScale(1);
      }
    };

    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, [props.isMobile, screenSize.width, screenSize.height]);

  // Calculate zoom and transform when editing a label on mobile
  const calculateLabelFocusTransform = () => {
    const defaultTransform = {
      scale: scale,
      translateX: 0,
      translateY: 0,
      transformOrigin: "top center",
    };

    if (!props.isMobile || !props.isDrawerExpanded) {
      return defaultTransform;
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = containerRef.current?.clientWidth ?? window.innerWidth;
    const navbarHeight = 60;
    const expandedDrawerHeight = viewportHeight * EXPANDED_HEIGHT_PERCENT;
    const drawerTopPosition = viewportHeight - expandedDrawerHeight;
    const availableHeight = drawerTopPosition - navbarHeight - 32; // 32px margin

    // If editing a static label, zoom in and center on it
    if (editingStaticLabel) {
      // Label position in poster coordinates (0-1 normalized)
      const labelX = editingStaticLabel.position.x;
      const labelY = editingStaticLabel.position.y;

      // Calculate zoom - be conservative to avoid cutting off wide labels
      // Labels near center (x ~0.5) can zoom more, labels near edges zoom less
      const distanceFromCenter = Math.abs(labelX - 0.5);
      // Zoom range: 1.3x for centered labels, down to 1.1x for edge labels
      const zoomMultiplier = 1.3 - distanceFromCenter * 0.4;
      const zoomedScale = Math.min(scale * zoomMultiplier, 1); // Don't exceed 1:1

      // Calculate where the label would be on screen after zoom
      // We want to position it in the vertical center of the available space
      const targetScreenY = navbarHeight + availableHeight * 0.4; // 40% down available space

      // Label's Y position in zoomed poster pixels
      const labelYInZoomedPoster = labelY * screenSize.height * zoomedScale;

      // Translate to put label at target position
      const translateY = targetScreenY - labelYInZoomedPoster - navbarHeight;

      // Calculate horizontal translation to keep label visible
      // The poster is centered by flexbox, so we need to shift it so the label is at screen center
      const zoomedWidth = screenSize.width * zoomedScale;
      const posterCenterX = zoomedWidth / 2;
      const labelXInZoomedPoster = labelX * zoomedWidth;

      // Distance from poster center to label (negative = label is left of center)
      const labelOffsetFromCenter = labelXInZoomedPoster - posterCenterX;

      // Shift poster opposite to label offset to center the label on screen
      let translateX = -labelOffsetFromCenter;

      // Clamp so poster edges don't go past viewport
      const maxTranslateX = Math.max(0, (zoomedWidth - viewportWidth) / 2);
      translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX));

      return {
        scale: zoomedScale,
        translateX,
        translateY,
        transformOrigin: "top center",
      };
    }

    // Fallback: translate based on poster bottom (original behavior)
    const scaledHeight = screenSize.height * scale;
    const currentTopPosition = navbarHeight + 16;
    const posterBottomPosition = currentTopPosition + scaledHeight;
    if (posterBottomPosition > drawerTopPosition) {
      const overlap = posterBottomPosition - drawerTopPosition;
      return {
        ...defaultTransform,
        translateY: -(overlap + 16),
      };
    }

    return defaultTransform;
  };

  const focusTransform = calculateLabelFocusTransform();

  const handleStageClick = () => {
    if (props.isMobile && props.isDrawerExpanded && props.onDismissDrawer) {
      props.onDismissDrawer();
    }
  };

  const scaledHeight = screenSize.height * focusTransform.scale;

  return (
    <div className={styles.mapContainer} ref={containerRef} onClick={handleStageClick}>
      <div
        className={styles.scaledPosterContainer}
        style={{
          height: props.isMobile ? scaledHeight : "auto",
          transform: props.isMobile
            ? `translate(${focusTransform.translateX}px, ${focusTransform.translateY}px)`
            : "none",
          transition: "transform 0.3s ease-out",
        }}
      >
        <div
          className={styles.mapAndLeftControlsContainer}
          style={
            props.isMobile
              ? {
                  width: screenSize.width,
                  height: screenSize.height,
                  transform: `scale(${focusTransform.scale})`,
                  transformOrigin: focusTransform.transformOrigin,
                  transition: "transform 0.3s ease-out",
                }
              : undefined
          }
        >
          {showZoomControls && <ZoomControl />}
          <Poster
            onShowContextualEditor={props.onShowContextualEditor}
            contextType={props.contextType}
            isMobile={props.isMobile}
          />
        </div>
      </div>
    </div>
  );
};

const ZoomControl = () => {
  const { zoomMapIn, zoomMapOut } = useEditorUI();
  return (
    <div className={styles.zoomControlContainer}>
      <button className={styles.zoomButton} onClick={() => zoomMapIn()}>
        +
      </button>
      <button className={styles.zoomButton} onClick={() => zoomMapOut()}>
        -
      </button>
    </div>
  );
};
